import { queryOptions } from '@tanstack/react-query'
import { z } from 'zod'

import type {
  CmsPageType,
  LoggerId,
  NotificationId,
  OrderId,
  OrganizationId,
  ProjectId,
  SiteId,
  UserId,
} from '@beaded/models'
import {
  blogIndexSchema,
  blogPostContentSchema,
  landingContentResponseSchema,
  landingIndexSchema,
  staticPageContentSchema,
  staticPageIndexSchema,
} from '@beaded/models'

import {
  cable,
  cablePrices,
  challenge,
  cmsPage,
  contactSubmission,
  forecastRequest,
  forecastSubscription,
  logger,
  loggerActivation,
  loggerFirmware,
  modem,
  notification,
  notificationNote,
  order,
  org,
  perm,
  project,
  reading,
  reconciliation,
  reportConfiguration,
  reportInstance,
  reportTemplate,
  search,
  sensor,
  service,
  serviceManager,
  site,
  seaIceForecast,
  siteForecastVisibility,
  user,
  userLanding,
} from 'api'
import type {
  LoggerLastTransmissionsResponse,
  LoggerMessagesResponse,
} from 'api/logger'

import { queryKeys } from './queryKeys'
import { SEARCH_PAGE_SIZE } from './searchPaging'

const blogPostCdnSchema = z.object({
  content: blogPostContentSchema,
  publishedAt: z.string().optional(),
})

const staticPageCdnSchema = z.object({
  content: staticPageContentSchema,
  publishedAt: z.string().optional(),
})

export const projectQueries = {
  detail: (id: ProjectId) =>
    queryOptions({
      queryKey: queryKeys.projects.detail(id),
      queryFn: ({ signal }) => project.get(Number(id), { signal }),
    }),
  list: (filter?: Record<string, unknown>) =>
    queryOptions({
      queryKey: queryKeys.projects.list(filter),
      queryFn: ({ signal }) =>
        project.getAll(filter ? { params: filter, signal } : { signal }),
    }),
  byOrg: (orgId: OrganizationId) =>
    queryOptions({
      queryKey: queryKeys.projects.byOrg(orgId),
      queryFn: ({ signal }) =>
        project.getAll({ params: { 'org.id': orgId }, signal }),
    }),
}

export const siteQueries = {
  detail: (id: SiteId) =>
    queryOptions({
      queryKey: queryKeys.sites.detail(id),
      queryFn: ({ signal }) => site.get(Number(id), { signal }),
    }),
  list: (filter?: Record<string, unknown>) =>
    queryOptions({
      queryKey: queryKeys.sites.list(filter),
      queryFn: ({ signal }) =>
        site.getAll(filter ? { params: filter, signal } : { signal }),
    }),
  byProject: (projectId: ProjectId) =>
    queryOptions({
      queryKey: queryKeys.sites.byProject(projectId),
      queryFn: ({ signal }) =>
        site.getAll({
          params: { 'project.id': projectId, sparkline: true },
          signal,
        }),
    }),
  changelog: (id: SiteId) =>
    queryOptions({
      queryKey: queryKeys.sites.changelog(id),
      queryFn: ({ signal }) => site.getChangeLog(id, { signal }),
    }),
}

export const forecastSubscriptionQueries = {
  forSite: (siteId: SiteId) =>
    queryOptions({
      queryKey: queryKeys.forecastSubscription.forSite(siteId),
      queryFn: ({ signal }) =>
        forecastSubscription.getStatus(siteId, { signal }),
    }),
}

export const forecastRequestQueries = {
  statusForSite: (siteId: SiteId) =>
    queryOptions({
      queryKey: queryKeys.forecastRequests.statusForSite(siteId),
      queryFn: ({ signal }) =>
        forecastRequest.getForecastStatus(siteId, { signal }),
    }),
  // #3844: paginated. params flow into the query key so each page is cached
  // independently and switching pages doesn't show stale data from another.
  adminReport: (params: { limit: number; offset: number }) =>
    queryOptions({
      queryKey: queryKeys.forecastRequests.adminReport(params),
      queryFn: () => forecastRequest.getAdminReport(params),
      // See siteForecastVisibilityQueries.adminReport below -- same reasoning.
      // This one is read-only, so stale data misinforms rather than causing a
      // wrong write, but it is a DB-only query and equally cheap to refresh.
      staleTime: 0,
      refetchOnMount: 'always',
      // refetchOnMount alone is NOT enough here. ForecastRequestsAdmin renders
      // both tab panels through Chakra Tabs.Content WITHOUT lazyMount/
      // unmountOnExit, so the panels are hidden rather than unmounted and each
      // useQuery observer mounts exactly once per page visit. Without this, an
      // admin who leaves the page open in a background tab never refetches at
      // all (the global default disables focus refetching), which is precisely
      // the "another admin changed it" case this override exists to fix.
      refetchOnWindowFocus: true,
    }),
}

export const seaIceForecastQueries = {
  forSite: (siteId: SiteId) =>
    queryOptions({
      queryKey: queryKeys.seaIceForecast.forSite(siteId),
      queryFn: ({ signal }) => seaIceForecast.getForSite(siteId, { signal }),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      // A page left open picks up a newly delivered run.
      refetchInterval: 15 * 60 * 1000,
    }),
}

export const siteForecastVisibilityQueries = {
  adminReport: () =>
    queryOptions({
      queryKey: queryKeys.siteForecastVisibility.adminReport(),
      queryFn: () => siteForecastVisibility.getAdminReport(),
      // The global default is staleTime/gcTime 24h with
      // refetchOnWindowFocus: false (lib/react-query.ts), which is right for
      // slow-moving customer data and wrong for an admin control surface: the
      // Show Forecast switches are rendered from this report AND write back to
      // it. Cached for a day, the table could show a site as off when another
      // admin (or another tab, or an out-of-band DB write) had already enabled
      // it -- and the admin would then toggle the wrong way. The mutation's
      // invalidateQueries only repairs the tab that performed it.
      //
      // Cost of always refetching is acceptable: this is a GLOBAL_ADMIN-only,
      // low-traffic page, and the expensive part (the S3 forecast-index scan
      // in forecastCacheIndex.ts) is a bulk paginated list, not per-site
      // calls. Revisit if that prefix grows enough to make the scan slow --
      // nothing prunes it today.
      staleTime: 0,
      refetchOnMount: 'always',
      // refetchOnMount alone is NOT enough here. ForecastRequestsAdmin renders
      // both tab panels through Chakra Tabs.Content WITHOUT lazyMount/
      // unmountOnExit, so the panels are hidden rather than unmounted and each
      // useQuery observer mounts exactly once per page visit. Without this, an
      // admin who leaves the page open in a background tab never refetches at
      // all (the global default disables focus refetching), which is precisely
      // the "another admin changed it" case this override exists to fix.
      refetchOnWindowFocus: true,
    }),
}

export const orgQueries = {
  detail: (id: OrganizationId) =>
    queryOptions({
      queryKey: queryKeys.orgs.detail(id),
      queryFn: ({ signal }) => org.get(id, { signal }),
    }),
  list: (filter?: Record<string, unknown>) =>
    queryOptions({
      queryKey: queryKeys.orgs.list(filter),
      queryFn: ({ signal }) =>
        org.getAll(filter ? { params: filter, signal } : { signal }),
    }),
  menu: () =>
    queryOptions({
      queryKey: queryKeys.menuProjections.org(),
      queryFn: () => org.getMenuProjection(),
    }),
}

export const loggerQueries = {
  // Without its events: a logger can have tens of thousands (11.7 MB for
  // logger 1457). The Logger page pages through them with `events` below.
  detail: (id: LoggerId) =>
    queryOptions({
      queryKey: queryKeys.loggers.detail(id),
      queryFn: ({ signal }) =>
        logger.get(id, { signal, params: { events: 'false' } }),
    }),
  /** The newest 100 events of the given types; the rest load in the background. */
  events: (id: LoggerId, types: string) =>
    queryOptions({
      queryKey: queryKeys.loggers.events(id, types),
      queryFn: ({ signal }) =>
        logger.getEvents(id, { types, limit: 100 }, { signal }),
    }),
  /** The newest Iridium messages of a logger. Global-view staff only. */
  messages: (id: LoggerId) =>
    queryOptions({
      queryKey: queryKeys.loggers.messages(id),
      queryFn: ({ signal }): Promise<LoggerMessagesResponse> =>
        logger.getMessages(id, { signal }),
    }),
  /** Every logger's last transmission and battery trend. Global-view staff only. */
  lastTransmissions: () =>
    queryOptions({
      queryKey: queryKeys.loggers.lastTransmissions(),
      queryFn: ({ signal }): Promise<LoggerLastTransmissionsResponse> =>
        logger.getLastTransmissions({ signal }),
    }),
  list: (filter?: Record<string, unknown>) =>
    queryOptions({
      queryKey: queryKeys.loggers.list(filter),
      queryFn: ({ signal }) =>
        logger.getAll(filter ? { params: filter, signal } : { signal }),
    }),
}

export const cableQueries = {
  detail: (id: string) =>
    queryOptions({
      queryKey: queryKeys.cables.detail(id),
      queryFn: ({ signal }) =>
        id.startsWith('sn')
          ? cable.getBySerialNumber(id.replace('sn', ''), { signal })
          : cable.get(id, { signal }),
    }),
  list: (filter?: Record<string, unknown>) =>
    queryOptions({
      queryKey: queryKeys.cables.list(filter),
      queryFn: ({ signal }) =>
        cable.getAll(filter ? { params: filter, signal } : { signal }),
    }),
}

export const modemQueries = {
  detail: (imei: string) =>
    queryOptions({
      queryKey: queryKeys.modems.detail(imei),
      queryFn: ({ signal }) => modem.get(imei, { signal }),
    }),
  list: (filter?: Record<string, unknown>) =>
    queryOptions({
      queryKey: queryKeys.modems.list(filter),
      queryFn: ({ signal }) =>
        modem.getAll(filter ? { params: filter, signal } : { signal }),
    }),
}

export const userQueries = {
  detail: (id: UserId) =>
    queryOptions({
      queryKey: queryKeys.users.detail(id),
      queryFn: ({ signal }) => user.get(id, { signal }),
    }),
  list: (filter?: Record<string, unknown>) =>
    queryOptions({
      queryKey: queryKeys.users.list(filter),
      queryFn: ({ signal }) =>
        user.getAll(filter ? { params: filter, signal } : { signal }),
    }),
}

// Metadata for one of the session user's API keys (#3687). Mirrors the
// apiKeyMetaSchema output in apps/api/src/ez/endpoints/userApiKey.ts -- the
// secret hash is never returned.
export interface ApiKeyMeta {
  id: string
  apiKeyType: string | null
  organizationId: number | null
  isReadOnly: boolean
  rateLimitPerMinute: number
  startedAt: string | null
  endedAt: string | null
  lastUse: string | null
}

export const apiKeyQueries = {
  mine: () =>
    queryOptions({
      queryKey: queryKeys.apiKeys.mine(),
      queryFn: async ({ signal }): Promise<ApiKeyMeta[]> => {
        const body = await user.listApiKeys({ signal })
        // resultHandler wraps the payload as { data: ... }; unwrap defensively.
        const payload = body?.data ?? body
        return (payload?.apiKeys ?? []) as ApiKeyMeta[]
      },
    }),
}

export const permQueries = {
  byUser: (userId: UserId) =>
    queryOptions({
      queryKey: queryKeys.perms.byUser(userId),
      queryFn: ({ signal }) => perm.get(userId, { signal }),
    }),
  projectUsers: (projectId: ProjectId) =>
    queryOptions({
      queryKey: queryKeys.permissions.byProject(projectId),
      queryFn: ({ signal }) =>
        perm.getProjectUsers(Number(projectId), { signal }),
    }),
  orgUsers: (orgId: OrganizationId) =>
    queryOptions({
      queryKey: queryKeys.permissions.byOrg(orgId),
      queryFn: ({ signal }) => perm.getOrgUsers(Number(orgId), { signal }),
    }),
}

export const readingQueries = {
  siteLines: (siteId: number) =>
    queryOptions({
      queryKey: queryKeys.readings.siteLines(siteId),
      queryFn: ({ signal }) =>
        reading.getLines('site', {
          params: { 'site.id': siteId },
          signal,
        }),
    }),
  projectLines: (projectId: ProjectId) =>
    queryOptions({
      queryKey: queryKeys.readings.projectLines(projectId),
      queryFn: ({ signal }) =>
        reading.getLines('project', {
          params: { 'project.id': projectId },
          signal,
        }),
    }),
  blueprintLines: (projectId: ProjectId) =>
    queryOptions({
      queryKey: queryKeys.readings.blueprintLines(projectId),
      queryFn: ({ signal }) =>
        reading.getLines('blueprint', {
          params: { 'project.id': projectId },
          signal,
        }),
    }),
}

export const notificationQueries = {
  detail: (id: NotificationId) =>
    queryOptions({
      queryKey: queryKeys.notifications.detail(id),
      queryFn: ({ signal }) => notification.get(id, { signal }),
    }),
  list: (filter?: Record<string, unknown>) =>
    queryOptions({
      queryKey: queryKeys.notifications.list(filter),
      queryFn: ({ signal }) =>
        notification.getAll(filter ? { params: filter, signal } : { signal }),
    }),
}

export const orderQueries = {
  detail: (id: OrderId) =>
    queryOptions({
      queryKey: queryKeys.orders.detail(id),
      queryFn: ({ signal }) => order.get(id, { signal }),
    }),
  list: (filter?: Record<string, unknown>) =>
    queryOptions({
      queryKey: queryKeys.orders.list(filter),
      queryFn: ({ signal }) =>
        order.getAll(filter ? { params: filter, signal } : { signal }),
    }),
  resyncPlan: (id: OrderId) =>
    queryOptions({
      queryKey: queryKeys.orders.resyncPlan(id),
      queryFn: ({ signal }) => order.resyncPlan(id, { signal }),
      enabled: false, // on-demand only, triggered via refetch()
    }),
}

export const challengeQueries = {
  list: (filter?: Record<string, unknown>) =>
    queryOptions({
      queryKey: queryKeys.challenges.list(filter),
      queryFn: ({ signal }) =>
        challenge.getAll(filter ? { params: filter, signal } : { signal }),
    }),
}

export const loggerActivationQueries = {
  list: (filter?: Record<string, unknown>) =>
    queryOptions({
      queryKey: queryKeys.loggerActivations.list(filter),
      queryFn: ({ signal }) =>
        loggerActivation.getAll(
          filter ? { params: filter, signal } : { signal },
        ),
    }),
}

export const sensorQueries = {
  byCable: (serial: string) =>
    queryOptions({
      queryKey: queryKeys.sensors.byCable(serial),
      queryFn: ({ signal }) => sensor.getAll({ params: { serial }, signal }),
    }),
}

export const serviceItemQueries = {
  list: (filter?: Record<string, unknown>) =>
    queryOptions({
      queryKey: queryKeys.serviceItems.list(filter),
      queryFn: ({ signal }) =>
        service.getAllServiceItems(
          filter ? { params: filter, signal } : { signal },
        ),
    }),
  // Items eligible for activation -- excludes those already bound to an active
  // (non-suspended) logger activation.
  available: (filter?: Record<string, unknown>) =>
    queryOptions({
      queryKey: queryKeys.serviceItems.list({ ...filter, available: true }),
      queryFn: ({ signal }) =>
        service.getAvailableServiceItems(
          filter ? { params: filter, signal } : { signal },
        ),
    }),
}

export const serviceContractQueries = {
  list: (filter?: Record<string, unknown>) =>
    queryOptions({
      queryKey: queryKeys.serviceContracts.list(filter),
      queryFn: ({ signal }) =>
        serviceManager.getAllServiceContracts(
          filter ? { params: filter, signal } : { signal },
        ),
    }),
}

export const productServiceQueries = {
  list: () =>
    queryOptions({
      queryKey: queryKeys.productServices.list(),
      queryFn: ({ signal }) => serviceManager.getAllProductServices({ signal }),
    }),
}

export const noteQueries = {
  list: () =>
    queryOptions({
      queryKey: queryKeys.notes.lists(),
      queryFn: ({ signal }) => notificationNote.getAll({ signal }),
    }),
}

export const reportTemplateQueries = {
  list: () =>
    queryOptions({
      queryKey: queryKeys.reportTemplates.lists(),
      queryFn: ({ signal }) => reportTemplate.getAll({ signal }),
    }),
  byType: (templateType: string) =>
    queryOptions({
      queryKey: queryKeys.reportTemplates.byType(templateType),
      queryFn: ({ signal }) =>
        reportTemplate.getByType(templateType, { signal }),
    }),
}

export const reportConfigurationQueries = {
  byProject: (projectId: ProjectId) =>
    queryOptions({
      queryKey: queryKeys.reportConfigurations.byProject(projectId),
      queryFn: ({ signal }) =>
        reportConfiguration.getByProject(projectId, { signal }),
    }),
}

export const reportInstanceQueries = {
  byProject: (projectId: ProjectId) =>
    queryOptions({
      queryKey: queryKeys.reportInstances.byProject(projectId),
      queryFn: ({ signal }) =>
        reportInstance.getByProject(projectId, { signal }),
    }),
}

export const searchQueries = {
  /** The first page of every type. */
  query: (term: string) =>
    queryOptions({
      queryKey: queryKeys.search.query(term),
      queryFn: ({ signal }) =>
        search.getPage(term, { limit: SEARCH_PAGE_SIZE }, { signal }),
    }),
}

export const firmwareQueries = {
  list: () =>
    queryOptions({
      queryKey: queryKeys.firmware.list(),
      queryFn: ({ signal }) => loggerFirmware.get({ signal }),
    }),
}

export const cablePriceQueries = {
  list: () =>
    queryOptions({
      queryKey: queryKeys.cables.prices(),
      queryFn: ({ signal }) => cablePrices.getAll({ signal }),
    }),
}

export const selfQueries = {
  me: () =>
    queryOptions({
      queryKey: queryKeys.self.all(),
      queryFn: async ({ signal }) => {
        const data = await user.getSelf({ signal })
        // "not logged in" is intentional -- Sentry beforeSend drops this string (lib/sentryBeforeSend.ts).
        if (data == null) throw new Error('not logged in')
        return data
      },
      staleTime: 1000 * 60 * 5,
      refetchInterval: 1000 * 60 * 5,
      retry: false,
    }),
}

export const publicQueries = {
  project: (id: ProjectId) =>
    queryOptions({
      queryKey: queryKeys.public.project(id),
      queryFn: ({ signal }) => project.publicPage.get(Number(id), { signal }),
    }),
  projects: () =>
    queryOptions({
      queryKey: queryKeys.public.projects(),
      queryFn: ({ signal }) => project.publicPage.getAll({ signal }),
    }),
  projectSites: (projectId: ProjectId) =>
    queryOptions({
      queryKey: queryKeys.public.sites({ 'project.id': projectId }),
      queryFn: ({ signal }) =>
        site.publicPage.getAll({
          params: { 'project.id': projectId },
          signal,
        }),
    }),
  org: (id: OrganizationId) =>
    queryOptions({
      queryKey: queryKeys.public.org(id),
      queryFn: ({ signal }) => org.publicPage.get(Number(id), { signal }),
    }),
  orgProjects: (orgId: OrganizationId) =>
    queryOptions({
      queryKey: queryKeys.public.projects({ 'org.id': orgId }),
      queryFn: ({ signal }) =>
        project.publicPage.getAll({ params: { 'org.id': orgId }, signal }),
    }),
  orgSites: (orgId: OrganizationId) =>
    queryOptions({
      queryKey: queryKeys.public.sites({ 'org.id': orgId }),
      queryFn: ({ signal }) =>
        site.publicPage.getAll({ params: { 'org.id': orgId }, signal }),
    }),
  site: (id: SiteId | string) =>
    queryOptions({
      queryKey: queryKeys.public.site(id),
      queryFn: ({ signal }) => site.publicPage.get(id, { signal }),
    }),
  chartData: (siteId: SiteId | string) =>
    queryOptions({
      queryKey: queryKeys.public.chartData(siteId),
      queryFn: ({ signal }) =>
        reading.publicPage.getLines('site', {
          params: { 'site.id': siteId },
          signal,
        }),
    }),
}

export const cmsPageQueries = {
  types: () =>
    queryOptions({
      queryKey: queryKeys.cmsPage.types(),
      queryFn: ({ signal }) => cmsPage.getTypes({ signal }),
    }),
  byType: (pageType: CmsPageType) =>
    queryOptions({
      queryKey: queryKeys.cmsPage.byType(pageType),
      queryFn: ({ signal }) => cmsPage.getAdmin(pageType, { signal }),
    }),
  history: (pageType: CmsPageType, id: string) =>
    queryOptions({
      queryKey: queryKeys.cmsPage.history(pageType, id),
      queryFn: ({ signal }) => cmsPage.getHistory(pageType, id, { signal }),
    }),
}

export const landingPageQueries = {
  bySlug: (slug: string) =>
    queryOptions({
      queryKey: queryKeys.landingPage.bySlug(slug),
      queryFn: async ({ signal }) => {
        const cdnUrl = import.meta.env.VITE_CONTENT_CDN_URL as
          | string
          | undefined
        if (cdnUrl) {
          try {
            const res = await fetch(`${cdnUrl}/landing/${slug}.json`, {
              signal,
            })
            if (res.ok) {
              const json: unknown = await res.json()
              const parsed = landingContentResponseSchema.safeParse(json)
              if (parsed.success) return parsed.data
            }
          } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError')
              throw err
            // CDN unreachable, fall through to API
          }
        }
        const apiData = await cmsPage.getPublicItem('landing', slug, {
          signal,
        })
        return { content: apiData.content, publishedAt: undefined }
      },
      staleTime: 60_000,
    }),
  index: () =>
    queryOptions({
      queryKey: queryKeys.landingPage.index(),
      queryFn: async ({ signal }) => {
        const cdnUrl = import.meta.env.VITE_CONTENT_CDN_URL as
          | string
          | undefined
        if (cdnUrl) {
          try {
            const res = await fetch(`${cdnUrl}/landing/index.json`, {
              signal,
            })
            if (res.ok) {
              const json: unknown = await res.json()
              const parsed = landingIndexSchema.safeParse(json)
              if (parsed.success) return parsed.data
            }
          } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError')
              throw err
            // CDN unreachable, fall through to API
          }
        }
        // Fallback: fetch via API list endpoint and transform
        const apiData = await cmsPage.getPublic('landing', { signal })
        const items =
          (
            apiData as {
              items?: Array<{
                slug: string
                title: string
                content: Record<string, unknown>
              }>
            }
          ).items ?? []
        return {
          pages: items.map((item) => ({
            slug: item.slug,
            title: item.title,
            industryName: (item.content?.industryName as string) ?? '',
          })),
        }
      },
      staleTime: 60_000,
    }),
}

export const blogPageQueries = {
  bySlug: (slug: string) =>
    queryOptions({
      queryKey: queryKeys.blogPage.bySlug(slug),
      queryFn: async ({ signal }) => {
        const cdnUrl = import.meta.env.VITE_CONTENT_CDN_URL as
          | string
          | undefined
        if (cdnUrl) {
          try {
            const res = await fetch(`${cdnUrl}/blog/${slug}.json`, { signal })
            if (res.ok) {
              const json: unknown = await res.json()
              const parsed = blogPostCdnSchema.safeParse(json)
              if (parsed.success) return parsed.data
            }
          } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError')
              throw err
            // CDN unreachable, fall through to API
          }
        }
        const apiData = await cmsPage.getPublicItem('blog', slug, { signal })
        return { content: apiData.content, publishedAt: undefined }
      },
      staleTime: 60_000,
    }),
  index: () =>
    queryOptions({
      queryKey: queryKeys.blogPage.index(),
      queryFn: async ({ signal }) => {
        const cdnUrl = import.meta.env.VITE_CONTENT_CDN_URL as
          | string
          | undefined
        if (cdnUrl) {
          try {
            const res = await fetch(`${cdnUrl}/blog/index.json`, { signal })
            if (res.ok) {
              const json: unknown = await res.json()
              const parsed = blogIndexSchema.safeParse(json)
              if (parsed.success) return parsed.data
            }
          } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError')
              throw err
            // CDN unreachable, fall through to API
          }
        }
        const apiData = await cmsPage.getPublic('blog', { signal })
        const items =
          (
            apiData as {
              items?: Array<{ slug: string; content: Record<string, unknown> }>
            }
          ).items ?? []
        return {
          posts: items.map((item) => ({
            slug: item.slug,
            title: (item.content?.title as string) ?? item.slug,
            author: (item.content?.author as string) ?? '',
            excerpt: (item.content?.excerpt as string) ?? '',
            heroImageUrl: (item.content?.heroImageUrl as string) ?? null,
            publishedAt: (item.content?.publishedAt as string) ?? null,
          })),
        }
      },
      staleTime: 60_000,
    }),
}

export const staticPageQueries = {
  bySlug: (slug: string) =>
    queryOptions({
      queryKey: queryKeys.staticPage.bySlug(slug),
      queryFn: async ({ signal }) => {
        const cdnUrl = import.meta.env.VITE_CONTENT_CDN_URL as
          | string
          | undefined
        if (cdnUrl) {
          try {
            const res = await fetch(`${cdnUrl}/pages/${slug}.json`, { signal })
            if (res.ok) {
              const json: unknown = await res.json()
              const parsed = staticPageCdnSchema.safeParse(json)
              if (parsed.success) return parsed.data
            }
          } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError')
              throw err
            // CDN unreachable, fall through to API
          }
        }
        const apiData = await cmsPage.getPublicItem('static', slug, { signal })
        return { content: apiData.content, publishedAt: undefined }
      },
      staleTime: 60_000,
    }),
  index: () =>
    queryOptions({
      queryKey: queryKeys.staticPage.index(),
      queryFn: async ({ signal }) => {
        const cdnUrl = import.meta.env.VITE_CONTENT_CDN_URL as
          | string
          | undefined
        if (cdnUrl) {
          try {
            const res = await fetch(`${cdnUrl}/pages/index.json`, { signal })
            if (res.ok) {
              const json: unknown = await res.json()
              const parsed = staticPageIndexSchema.safeParse(json)
              if (parsed.success) return parsed.data
            }
          } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError')
              throw err
            // CDN unreachable, fall through to API
          }
        }
        const apiData = await cmsPage.getPublic('static', { signal })
        const items =
          (
            apiData as {
              items?: Array<{ slug: string; content: Record<string, unknown> }>
            }
          ).items ?? []
        return {
          pages: items.map((item) => ({
            slug: item.slug,
            title: (item.content?.title as string) ?? item.slug,
          })),
        }
      },
      staleTime: 60_000,
    }),
}

export const contactSubmissionQueries = {
  list: (params?: { page?: number; unreadOnly?: boolean }) =>
    queryOptions({
      queryKey: queryKeys.contactSubmissions.list(params),
      queryFn: ({ signal }) =>
        contactSubmission.getAll(
          params
            ? { page: params.page, unreadOnly: params.unreadOnly }
            : undefined,
          { signal },
        ),
    }),
}

export const userLandingQueries = {
  landing: () =>
    queryOptions({
      queryKey: queryKeys.userLanding.landing(),
      queryFn: ({ signal }) => userLanding.getLandingData({ signal }),
    }),
  checkSiteFavorite: (siteId: SiteId) =>
    queryOptions({
      queryKey: queryKeys.userLanding.checkSiteFavorite(siteId),
      queryFn: ({ signal }) =>
        userLanding.checkSiteFavorite(siteId, { signal }),
    }),
  checkProjectFavorite: (projectId: ProjectId) =>
    queryOptions({
      queryKey: queryKeys.userLanding.checkProjectFavorite(projectId),
      queryFn: ({ signal }) =>
        userLanding.checkProjectFavorite(projectId, { signal }),
    }),
}

export const reconciliationQueries = {
  discrepancies: () =>
    queryOptions({
      queryKey: queryKeys.reconciliation.discrepancies(),
      queryFn: () => reconciliation.getDiscrepancies(),
      enabled: false, // on-demand only via refetch()
    }),
  serialCheck: (windowDays = 7) =>
    queryOptions({
      queryKey: queryKeys.reconciliation.serialCheck(windowDays),
      queryFn: () => reconciliation.checkSerials(windowDays),
      enabled: false, // on-demand only
    }),
}
